// ============================================
// Fashion Plate — Prompt Maker Service
//
// Core classification call: interprets operator
// instructions (via Tap-to-Instruct regions) and
// outputs structured changes to the nurse config.
// Uses a single Gemini call to classify intent
// and produce context + trait updates.
// ============================================

import { geminiGenerate } from '../ai/providers/gemini';
import type {
  PromptMakerInput,
  PromptMakerOutput,
  GarmentState,
  GarmentPiece,
} from '../../types/fashionPlate';
import type { ContextLayer } from '../../types/patientJourney';
import { GARMENT_REGISTRY, resolveGarmentLabel } from '../../data/fashionPlate/garmentRegistry';

// -------------------------------------------------------
// Constants
// -------------------------------------------------------

const MODEL = 'gemini-2.5-flash';
const TEMPERATURE = 0.3;
const MAX_TOKENS = 1024;

const ALL_GARMENT_PIECES: GarmentPiece[] = [
  'shoes', 'heart', 'brain', 'hat', 'dress', 'accessories',
];

// -------------------------------------------------------
// System Prompt
// -------------------------------------------------------

const SYSTEM_PROMPT = `You are the Fashion Plate Prompt Maker — a classification engine that interprets operator instructions and produces structured updates to an AI nurse's configuration.

## Tap Region Routing

The operator may tap a region on the nurse avatar before typing their instruction. The tap region tells you which aspect of the nurse to focus on:

- **brain**: Knowledge, evidence depth, clinical authority, health literacy adaptation. Changes should target healthKnowledge, supplementExperience, aiComfort, journeyStage, adherenceLevel, or the dataDensity / explanationDepth trait sliders.
- **heart**: Empathy, emotional intelligence, relational warmth. Changes should target baselineStress, recentLifeEvent, partnerStatus, caregiverBurden, socialSupport, or the empathy trait slider.
- **personality**: Tone, register, communication style, pacing. Changes should target motivation, timeOfDay, season, cyclePhase, timeSinceLastInteraction, or the productTiming / verificationFreq / urgencyCalibration trait sliders.
- **dress**: Everything — full outfit change. Any context variable or trait slider is fair game.

If no tap region is provided, treat it as "dress" (full access).

## Context Layer Variables (17 total)

You may update any of the following context variables. Use ONLY the allowed values listed.

### Temporal (4)
- timeOfDay: "morning" | "afternoon" | "evening" | "late_night"
- cyclePhase: "follicular" | "ovulatory" | "luteal" | "menstrual" | "postmenopausal" | "not_applicable"
- season: "spring" | "summer" | "fall" | "winter"
- timeSinceLastInteraction: "first_visit" | "days" | "weeks" | "months"

### Emotional (3)
- baselineStress: "low" | "moderate" | "high" | "crisis"
- recentLifeEvent: "none" | "divorce" | "job_loss" | "bereavement" | "new_diagnosis" | "new_baby" | "relocation"
- motivation: "desperate" | "concerned" | "curious" | "proactive" | "skeptical"

### Relational (3)
- partnerStatus: "single" | "partnered" | "divorced" | "widowed"
- caregiverBurden: "none" | "light" | "heavy"
- socialSupport: "strong" | "moderate" | "isolated"

### Financial (2)
- budgetSensitivity: "low" | "moderate" | "high" | "severe"
- insuranceContext: "insured" | "uninsured" | "hsa_fsa_eligible"

### Literacy (3)
- healthKnowledge: "minimal" | "basic" | "informed" | "expert"
- supplementExperience: "none" | "some" | "extensive"
- aiComfort: "distrustful" | "cautious" | "neutral" | "comfortable"

### Clinical (2)
- journeyStage: "first_visit" | "on_protocol" | "retesting" | "post_escalation" | "re_engagement"
- adherenceLevel: "high" | "moderate" | "low" | "unknown"

## Trait Sliders (6)

You may adjust any of these numeric sliders (0-100):

- empathy (0-100): Guide register intensity. 0 = data-only robot, 100 = maximum warmth.
- dataDensity (0-100): Biomarker references per response. 0 = no numbers, 100 = full data dump.
- explanationDepth (0-100): Jargon threshold. 0 = 5th-grade reading level, 100 = peer-reviewed journal.
- productTiming (0-100): Turn number to introduce formulary. 0 = immediate product mention, 100 = wait as long as possible.
- verificationFreq (0-100): Follow-up question frequency. 0 = never checks in, 100 = checks every response.
- urgencyCalibration (0-100): Phase transition speed. 0 = calm and unhurried, 100 = urgent and fast-paced.

## Your Task

Given the operator's instruction and the current nurse state, determine:
1. Which context variables should change (if any)
2. Which trait sliders should adjust (if any)
3. A short confirmation message the nurse would say to acknowledge the change

## Output Format

Respond with ONLY valid JSON (no markdown fencing, no commentary):

{
  "contextUpdates": { ... },
  "traitAdjustments": { ... } or null,
  "nurseConfirmation": "..."
}

Rules:
- contextUpdates: only include variables that CHANGE. Omit unchanged variables. Use {} if nothing changes.
- traitAdjustments: only include sliders that CHANGE. Use null if no trait changes.
- nurseConfirmation: a 1-2 sentence first-person acknowledgment from the nurse. Keep it natural and brief. Example: "Got it — I'll dial back the clinical jargon and keep things simple."
- Do NOT invent context values outside the allowed enums above.
- Do NOT set trait values outside 0-100.`;

// -------------------------------------------------------
// Build the user message with current state
// -------------------------------------------------------

function buildUserMessage(input: PromptMakerInput): string {
  const parts: string[] = [];

  if (input.tapRegion) {
    parts.push(`[Tap Region: ${input.tapRegion}]`);
  }

  parts.push(`## Current Context\n${JSON.stringify(input.currentContext, null, 2)}`);

  if (input.currentTraitProfile) {
    parts.push(`## Current Trait Profile\n${JSON.stringify(input.currentTraitProfile, null, 2)}`);
  }

  parts.push(`## Current Garments`);
  for (const g of input.currentGarments) {
    parts.push(`- ${g.piece}: ${g.label} (${g.intensity})`);
  }

  parts.push(`## Operator Instruction\n${input.instruction}`);

  return parts.join('\n\n');
}

// -------------------------------------------------------
// Build full GarmentState[] from resolved labels
// -------------------------------------------------------

function buildGarmentStates(mergedContext: Partial<ContextLayer>): GarmentState[] {
  return ALL_GARMENT_PIECES.map((piece) => {
    const resolved = resolveGarmentLabel(piece, mergedContext);
    const registryEntry = GARMENT_REGISTRY.find((e) => e.piece === piece);

    // Build activeVariables: the context values relevant to this garment piece
    const activeVariables: Record<string, string> = {};
    if (registryEntry) {
      for (const varKey of registryEntry.variables) {
        const val = mergedContext[varKey as keyof ContextLayer];
        if (val !== undefined) {
          activeVariables[varKey] = val;
        }
      }
    }

    return {
      piece,
      label: resolved.label,
      intensity: resolved.intensity,
      contextCategory: registryEntry?.contextCategory ?? 'Unknown',
      activeVariables,
    };
  });
}

// -------------------------------------------------------
// Strip markdown code fences from LLM response
// -------------------------------------------------------

function stripCodeFences(raw: string): string {
  let cleaned = raw.trim();
  // Remove ```json ... ``` or ``` ... ```
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '');
  }
  return cleaned.trim();
}

// -------------------------------------------------------
// Main entry point
// -------------------------------------------------------

export async function runPromptMaker(input: PromptMakerInput): Promise<PromptMakerOutput> {
  const userMessage = buildUserMessage(input);

  let rawResponse: string;
  try {
    rawResponse = await geminiGenerate(
      MODEL,
      SYSTEM_PROMPT,
      userMessage,
      TEMPERATURE,
      MAX_TOKENS,
    );
  } catch (err) {
    // LLM call failed — return current state unchanged
    console.error('[PromptMaker] Gemini call failed:', err);
    return {
      layerUpdates: {},
      garmentChanges: [...input.currentGarments],
      traitAdjustments: undefined,
      contextUpdates: undefined,
      nurseConfirmation: "I didn't catch that. Could you rephrase?",
    };
  }

  // Parse the JSON response
  let parsed: {
    contextUpdates?: Partial<ContextLayer>;
    traitAdjustments?: Record<string, number> | null;
    nurseConfirmation?: string;
  };

  try {
    const cleaned = stripCodeFences(rawResponse);
    parsed = JSON.parse(cleaned);
  } catch {
    console.error('[PromptMaker] JSON parse failed. Raw response:', rawResponse);
    return {
      layerUpdates: {},
      garmentChanges: [...input.currentGarments],
      traitAdjustments: undefined,
      contextUpdates: undefined,
      nurseConfirmation: "I didn't catch that. Could you rephrase?",
    };
  }

  // Merge contextUpdates with currentContext
  const contextUpdates = parsed.contextUpdates ?? {};
  const mergedContext: Partial<ContextLayer> = {
    ...input.currentContext,
    ...contextUpdates,
  };

  // Resolve garment states from merged context
  const garmentChanges = buildGarmentStates(mergedContext);

  // Extract trait adjustments (null from Gemini means no changes)
  const traitAdjustments = parsed.traitAdjustments ?? undefined;

  // Confirmation message
  const nurseConfirmation =
    parsed.nurseConfirmation || "Got it — I've updated my approach.";

  return {
    layerUpdates: {},  // Phase 2: prompt text manipulation
    garmentChanges,
    traitAdjustments: traitAdjustments || undefined,
    contextUpdates: Object.keys(contextUpdates).length > 0 ? contextUpdates : undefined,
    nurseConfirmation,
  };
}
